/**
 * useScrollDirection — emits "up" | "down" | "idle" based on the user's
 * recent scroll direction. Used by FAB to hide on scroll-down (gives more
 * screen real-estate while reading) and reveal on scroll-up (Material 3
 * BottomAppBar pattern).
 *
 * Implementation notes:
 *   - Listens to `window.scroll` passive (perf-friendly).
 *   - Direction commits only after `threshold` pixels of continuous travel
 *     in one direction — avoids jitter from inertial scroll micro-bounces.
 *   - Idle reset after `idleMs` of no scroll events — so a paused page
 *     restores the FAB even if the last gesture was a scroll-down.
 *   - Initial state is "idle" so the FAB is always visible on mount.
 */
import { useEffect, useRef, useState } from "react";

export type ScrollDirection = "up" | "down" | "idle";

export function useScrollDirection(
  threshold: number = 8,
  idleMs: number = 800,
): ScrollDirection {
  const [direction, setDirection] = useState<ScrollDirection>("idle");
  const lastY = useRef(typeof window !== "undefined" ? window.scrollY : 0);
  const accumulator = useRef(0);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastY.current;
      lastY.current = currentY;

      // Reset accumulator if direction flipped — start fresh count.
      if ((delta > 0 && accumulator.current < 0) || (delta < 0 && accumulator.current > 0)) {
        accumulator.current = 0;
      }
      accumulator.current += delta;

      if (accumulator.current >= threshold) {
        setDirection("down");
        accumulator.current = 0;
      } else if (accumulator.current <= -threshold) {
        setDirection("up");
        accumulator.current = 0;
      }

      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => setDirection("idle"), idleMs);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [threshold, idleMs]);

  return direction;
}
